import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { logger } from '../utils/logger';

interface CompanyFeatures {
  membership_enabled: boolean;
  events_enabled: boolean;
  coupons_enabled: boolean;
}

interface CompanyInfo {
  company_id: string;
  company_name: string;
  display_name?: string;
  tier: string;
  status: string;
  settings?: {
    features?: CompanyFeatures;
  };
}

interface CompanyContextType {
  company: CompanyInfo | null;
  features: CompanyFeatures;
  loading: boolean;
  error: string | null;
  refreshCompanyInfo: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshCompanyInfo = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/companies/me');
      const companyData = response.data;
      setCompany(companyData);
    } catch (err) {
      setError('Failed to load company information');
      logger.error('Error fetching company info:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshCompanyInfo();
    } else {
      setCompany(null);
    }
  }, [user, refreshCompanyInfo]);

  const value = useMemo(() => {
    const features: CompanyFeatures = {
      membership_enabled: company?.settings?.features?.membership_enabled ?? true,
      events_enabled: company?.settings?.features?.events_enabled ?? true,
      coupons_enabled: company?.settings?.features?.coupons_enabled ?? true
    };

    return {
      company,
      features,
      loading,
      error,
      refreshCompanyInfo
    };
  }, [company, loading, error, refreshCompanyInfo]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
